import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Button, Flex, Form, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { MarketService } from "../../services/MarketService";
import { mdlMarketDetailRequest } from "../../models/service-models/market/MarketDetailRequest";
import { mdlMarketUpdateRequest } from "../../models/service-models/market/MarketUpdateRequest";
import { mdlMarketCreateRequest } from "../../models/service-models/market/MarketCreateRequest";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { CountrySelect } from "../common/CountrySelect";
import { CurrencySelect } from "../common/CurrencySelect";
import { SystemMarketSelect } from "../common/SystemMarketSelect";
import { mdlSystemMarket } from "../../models/domain/SystemMarket";
import { mdlServiceResponse } from "../../models/service-models/common/ServiceResponse";
import { mdlSystemMarketListResponse } from "../../models/service-models/market/SystemMarketListResponse";

export type MarketDetailProps = {
    id?: string;
    hotelId?: string;
    callback?: Function;
}


export const MarketDetail = (props: MarketDetailProps) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [systemMarketList, setSystemMarketList] = useState<mdlSystemMarket[]>();

    useEffect(() => {
        if (props.id)
            getMarketDetail();
    }, [props]);

    const {
        data,
        isLoading,
        isError,
        isFetched,
        refetch: getMarketDetail
    } = useQuery(
        ["getMarketDetail", props.id],
        async () => {
            if (props.id) {
                var request: mdlMarketDetailRequest = { id: props.id };
                form.resetFields();
                return MarketService.Detail(request);
            }
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!props.id,
            onSuccess(data) {
                form.setFieldsValue({
                    countries: data?.body?.data?.hotelMarketCountries?.map(x => x.countryId),
                    currencies: data?.body?.data?.hotelMarketCurrencies?.map(x => x.currencyCode),
                    marketId: data?.body?.data?.marketId
                });
            },
        }
    );

    const { mutateAsync: saveMarket, isLoading: mutateLoading, data: mutateData } = useMutation(
        async (data: mdlMarketCreateRequest | mdlMarketUpdateRequest) => {
            const selectedMarket = systemMarketList?.find(market => market.marketId === data.marketId);
            if (selectedMarket) {
                // Set required market related fields.
                data.marketId = selectedMarket.marketId;
                data.marketCode = selectedMarket.code;
                data.marketName = selectedMarket.name;
            }
            if (props.id) {
                (data as mdlMarketUpdateRequest).id = props.id;
            }
            return props.id ? MarketService.Update(data as mdlMarketUpdateRequest) : MarketService.Create(data as mdlMarketCreateRequest)
        },
        {
            onMutate(variables) {
                variables.hotelId = props.hotelId;
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success && data.body) {
                    if (data.body.isSuccessful === true) {
                        toast.success(t("common.success"));
                        form.resetFields();
                        props.callback && props.callback();
                    }
                    else {
                        toast.error(data.body.message);
                    }
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );

    const onSystemMarketLoad = (systemMarkets: mdlSystemMarket[]) => {
        setSystemMarketList(systemMarkets);
    }

    return (
        <>
            {
                !isLoading
                &&
                ((props.id && isFetched) || !props.id)
                &&
                <Spin spinning={isLoading || mutateLoading}>
                    <Form
                        form={form}
                        name="market-form"
                        layout="vertical"
                        onFinish={async (values) => await saveMarket(values)}
                        autoComplete="off"
                    >
                        <Form.Item<mdlMarketUpdateRequest | mdlMarketCreateRequest>
                            label={t("definitions.markets.systemMarkets")}
                            name="marketId"
                            rules={[{ required: true, message: t("common.required") }, { type: "string", message: t("definitions.markets.systemMarketRequired") }]}
                        >
                            <SystemMarketSelect onLoad={onSystemMarketLoad} onChange={(value: string) => form.setFieldsValue({ marketId: value })} />
                        </Form.Item>

                        <Form.Item<mdlMarketUpdateRequest | mdlMarketCreateRequest>
                            label={t("definitions.markets.countries")}
                            name="countries"
                            rules={[{ required: true, message: t("common.required") }, { type: "array", message: t("definitions.markets.countryRequired") }]}
                        >
                            <CountrySelect mode="multiple" onChange={(value: string[]) => form.setFieldsValue({ countries: value })} />
                        </Form.Item>

                        <Form.Item<mdlMarketUpdateRequest | mdlMarketCreateRequest>
                            label={t("definitions.markets.currencies")}
                            name="currencies"
                            rules={[{ required: true, message: t("common.required") }, { type: "array", message: t("definitions.markets.currencyRequired") }]}
                        >
                            <CurrencySelect mode="multiple" onChange={(value: string[]) => form.setFieldsValue({ currencies: value })} />
                        </Form.Item>


                        <Form.Item>
                            <Flex align={"center"} justify="flex-end">
                                <Button type="primary" htmlType="submit">{t("common.save")}</Button>
                            </Flex>

                        </Form.Item>
                    </Form>
                </Spin>
            }
        </>
    )
}
